import { executeCapability } from '../utils/capabilityExecute'

export type ManagedResourceType = 'FAQ' | 'Policy' | 'Legal'

/** ATX heading depth: 1 = #, 2 = ##, … 6 = ###### — each policy starts at this level */
export type PolicyMarkdownSplitLevel = 1 | 2 | 3 | 4 | 5 | 6

export interface ManagedResourceItem {
  id: string
  type: ManagedResourceType
  title: string
  content: string
  createdAt: string
  updatedAt: string
  importBatchId?: string
  sourceFileName?: string
  /** Extra labels on a policy (manual or curated); not tied to import batch */
  tags?: string[]
  /** Resolved from importBatchTags for policies with an import batch (API-only, not stored on row) */
  batchTag?: string
}

export type ImportBatchTagsMap = Record<string, string>

export interface ResourceContactEntry {
  employeeId: string
  name: string
  preferredName?: string | null
  position: string
  phone: string
  email: string
  workEmail?: string | null
  workPhone?: string | null
  department?: string
  supervisor?: string
  createdAt?: string | null
  updatedAt?: string | null
}

export type ResourceContactCert = {
  type: string
  label?: string
  status: string
  expirationDate: string | Date | null
  hasFile?: boolean
}

export type ResourceCertCheckResult = {
  employeeId: string
  certs: ResourceContactCert[]
}

export interface PolicyMarkdownPreviewItem {
  title: string
  bodyMarkdown: string
  anchor: string | null
  /** True for intro/TOC block before the first heading at the chosen depth */
  preamble?: boolean
}

export interface PolicyMarkdownPreview {
  splitLevel: PolicyMarkdownSplitLevel
  items: PolicyMarkdownPreviewItem[]
  warnings: string[]
}

export interface PolicyMarkdownImportResult {
  importBatchId: string
  sourceFileName: string | null
  splitLevel: PolicyMarkdownSplitLevel
  createdCount: number
  items: ManagedResourceItem[]
  warnings: string[]
}

class ResourcesService {
  async listContacts(q?: string): Promise<ResourceContactEntry[]> {
    return executeCapability<ResourceContactEntry[]>({
      app: 'resources',
      capability: 'resources.contactsList',
      input: { q: q || '' },
    })
  }

  async listContactCerts(employeeId: string): Promise<ResourceContactCert[]> {
    const data = await executeCapability<ResourceCertCheckResult>({
      app: 'resources',
      capability: 'resources.certCheck',
      input: { employeeId },
    })
    return Array.isArray(data?.certs) ? data.certs : []
  }

  async listFaqPolicies(): Promise<{ items: ManagedResourceItem[]; importBatchTags: ImportBatchTagsMap }> {
    const res = await executeCapability<{
      items: ManagedResourceItem[]
      importBatchTags?: ImportBatchTagsMap
    }>({
      app: 'resources',
      capability: 'resources.faqPolicies.list',
      input: {},
    })
    return {
      items: Array.isArray(res?.items) ? res.items : [],
      importBatchTags: res?.importBatchTags && typeof res.importBatchTags === 'object' ? res.importBatchTags : {},
    }
  }

  async setImportBatchTag(importBatchId: string, tag: string): Promise<{ importBatchId: string; tag: string | null }> {
    return executeCapability<{ importBatchId: string; tag: string | null }>({
      app: 'resources',
      capability: 'resources.importBatchTags.set',
      input: { importBatchId, tag },
    })
  }

  async createFaqPolicy(payload: {
    type: ManagedResourceType
    title: string
    content: string
    tags?: string[]
  }): Promise<ManagedResourceItem> {
    return executeCapability<ManagedResourceItem>({
      app: 'resources',
      capability: 'resources.faqPolicies.create',
      input: {
        type: payload.type,
        title: payload.title,
        content: payload.content,
        ...(payload.type === 'Policy' && payload.tags !== undefined ? { tags: payload.tags } : {}),
      },
    })
  }

  async updateFaqPolicy(payload: {
    id: string
    type: ManagedResourceType
    title: string
    content: string
    tags?: string[]
  }): Promise<ManagedResourceItem> {
    return executeCapability<ManagedResourceItem>({
      app: 'resources',
      capability: 'resources.faqPolicies.update',
      input: {
        id: payload.id,
        type: payload.type,
        title: payload.title,
        content: payload.content,
        ...(payload.type === 'Policy' && payload.tags !== undefined ? { tags: payload.tags } : {}),
      },
    })
  }

  async deleteFaqPolicy(id: string): Promise<void> {
    await executeCapability({
      app: 'resources',
      capability: 'resources.faqPolicies.delete',
      input: { id },
    })
  }

  async deleteFaqPoliciesByImportBatch(importBatchId: string): Promise<{ deletedCount: number }> {
    return executeCapability<{ deletedCount: number }>({
      app: 'resources',
      capability: 'resources.faqPolicies.deleteByImportBatch',
      input: { importBatchId },
    })
  }

  async previewPolicyMarkdown(markdown: string, splitLevel?: PolicyMarkdownSplitLevel): Promise<PolicyMarkdownPreview> {
    return executeCapability<PolicyMarkdownPreview>({
      app: 'resources',
      capability: 'resources.policyMarkdown.preview',
      input: splitLevel !== undefined ? { markdown, splitLevel } : { markdown },
    })
  }

  async importPolicyMarkdown(payload: {
    markdown: string
    sourceFileName?: string
    splitLevel?: PolicyMarkdownSplitLevel
    /** If omitted, server imports every parsed section. If provided, must be non-empty. */
    selectedIndexes?: number[]
  }): Promise<PolicyMarkdownImportResult> {
    return executeCapability<PolicyMarkdownImportResult>({
      app: 'resources',
      capability: 'resources.policyMarkdown.import',
      input: {
        markdown: payload.markdown,
        sourceFileName: payload.sourceFileName,
        ...(payload.splitLevel !== undefined ? { splitLevel: payload.splitLevel } : {}),
        ...(payload.selectedIndexes !== undefined ? { selectedIndexes: payload.selectedIndexes } : {}),
      },
    })
  }
}

export const resourcesService = new ResourcesService()

export function policyHeadingDepthLabel(depth: PolicyMarkdownSplitLevel): string {
  return '#'.repeat(depth)
}
